import { GENESUS_AUTOMATIC_COHORT, WORKFLOW_DEFAULTS } from 'src/services/analysisStart'
import { normaliseWorkflow } from 'src/views/NewAnalysis/model/workflow'

// ----------------------------------------------------------------------
// Reading a start body back into the New analysis panel's workflow.
//
// Every start body is stored verbatim as analysis_parameters (libra GenericAnnotateView)
// and /analysis/list returns it per row. workflowFromParameters inverts
// buildStartRequest (src/services/analysisStart.js) for the batch-level choices only:
// the analysis, input, mode, sequencing, kit, CNV, long-read options, reference and
// interpretation. Sample fields (HPO, clinical history, lanes, the normal sample,
// sex, age) are left out. A body it cannot read for certain gives null, never a
// guess: an unknown sequencing or CNV type (the legacy 'GENESUS' and 'GATK'), an
// exome without a kit, a VCF without a reference, a CNV cohort.
//
// The backend adds annotation.panelName (the kit's visible name) to FASTQ bodies; it
// becomes kitName, which is only for display.
// ----------------------------------------------------------------------

const INPUT_OF_ANNOTATION_TYPE = { FASTQ: 'fastq', ONT_BAM: 'ont', PACBIO_BAM: 'pacbio', VCF: 'vcf' }

// targetedType as libra reads it (spaces dropped, any case): the panel sends
// "Whole Exome", the older upload dialogs "WholeExome".
const SEQ_OF_TARGETED = { wholeexome: 'wes', wes: 'wes', clinicalexome: 'ces' }
const seqOfTargeted = (value) =>
  typeof value === 'string' ? SEQ_OF_TARGETED[value.replace(/ /g, '').toLowerCase()] || null : null

// analysisType as libra reads it (SOMATIC_ANALYSIS_TYPES).
const MODE_OF_SOMATIC_TYPE = { TumorOnly: 'to', 'Tumor Only': 'to', TumorNormal: 'tn', 'Tumor Normal': 'tn' }

// Only the CNV types the backend accepts today. The legacy 'GENESUS' and 'GATK' do
// not say which of today's methods they were, so bodies with them are not read.
const CNV_METHOD_OF_TYPE = {
  'GENESUS-CNV': 'genesus-cnv',
  EXOMEDEPTH: 'exomedepth',
  ECOLE: 'ecole',
}

const REFERENCES = ['GRCh38', 'GRCh37']

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value)
const text = (value) => (typeof value === 'string' ? value.trim() : '')
const hasId = (value) => (typeof value === 'string' && value.trim() !== '') || Number.isFinite(value)

/** A kit's display name: its visible name without a trailing ".bed". */
export const kitDisplayName = (name) => text(name).replace(/\.bed(\.gz)?$/i, '')

const interpretationOf = (annotation) => (annotation.runAI === true ? 'ai' : 'standard')

// The default tumour type (OncoTree code and name), or null.
const tumourOf = (a) => {
  const code = text(a.oncotreeCode)
  return code ? { oncotree_code: code, name: text(a.oncotreeName) || code } : null
}

const fastqWorkflow = (a) => {
  let analysis
  let mode = WORKFLOW_DEFAULTS.mode
  if (a.analysisType === 'Germline') analysis = 'germline'
  else if (MODE_OF_SOMATIC_TYPE[a.analysisType]) {
    analysis = 'somatic'
    mode = MODE_OF_SOMATIC_TYPE[a.analysisType]
  } else return null

  // libra prices (and so starts) only these two sequencing types.
  let seq = null
  if (a.type === 'WholeGenome') seq = 'wgs'
  else if (a.type === 'Targeted') seq = seqOfTargeted(a.targetedType)
  if (!seq) return null

  const exome = seq !== 'wgs'
  const kitId = exome ? text(a.panel) : ''
  if (exome && !kitId) return null

  const wf = {
    analysis,
    input: 'fastq',
    mode,
    seq,
    kitId,
    kitName: exome ? kitDisplayName(a.panelName) : '',
  }
  if (analysis === 'somatic') return { ...wf, tumourType: tumourOf(a) }
  wf.interpretation = interpretationOf(a)
  // The backend runs CNV on germline exomes only; anything else ignored runCnv.
  if (exome && a.runCnv === true) {
    // libra takes the type in either case.
    const method = typeof a.cnv_type === 'string' ? CNV_METHOD_OF_TYPE[a.cnv_type.toUpperCase()] : undefined
    if (!method) return null
    wf.cnv = true
    wf.cnvMethod = method
    if (method === 'genesus-cnv') wf.cnvCohortId = hasId(a.cohort_id) ? a.cohort_id : GENESUS_AUTOMATIC_COHORT
    else if (method === 'exomedepth') wf.cnvCohortId = hasId(a.cohort_id) ? a.cohort_id : ''
  }
  return wf
}

const vcfWorkflow = (a) => {
  if (!REFERENCES.includes(a.reference)) return null
  if (a.isSomatic === true)
    return { analysis: 'somatic', input: 'vcf', reference: a.reference, tumourType: tumourOf(a) }
  return { analysis: 'germline', input: 'vcf', reference: a.reference, interpretation: interpretationOf(a) }
}

// The backend reads a missing runCnv / runSv / runMod as false.
const longReadWorkflow = (a, input) => ({
  analysis: 'germline',
  input,
  lrCnv: a.runCnv === true,
  lrSv: a.runSv === true,
  lrMeth: a.runMod === true,
  interpretation: interpretationOf(a),
})

// "Carrier Analysis: Couple screening" -> "Couple screening".
const carrierTitleOf = (analysisName) => text(analysisName).replace(/^Carrier Analysis:\s*/i, '')

const carrierWorkflow = (params, analysisName) => {
  if (!hasId(params.carrierConfigId)) return null
  const members = Array.isArray(params.annotation) ? params.annotation.length : 0
  return {
    analysis: 'carrier',
    carrierConfigId: params.carrierConfigId,
    carrierConfigTitle: carrierTitleOf(analysisName),
    carrierSamplesRequired: members || null,
  }
}

const complete = (fields) => normaliseWorkflow({ ...WORKFLOW_DEFAULTS, ...fields })

/**
 * The panel's workflow for one stored start body, or null when the body cannot be
 * read for certain. `analysisType` is the row's analysis_type; `analysisName` its
 * analysis_name (only a carrier's panel title is read from it).
 */
export function workflowFromParameters(params, analysisType, analysisName) {
  if (!isObject(params)) return null
  const type = INPUT_OF_ANNOTATION_TYPE[params.annotation_type] ? params.annotation_type : analysisType
  const input = INPUT_OF_ANNOTATION_TYPE[type]
  if (input) {
    const a = params.annotation
    if (!isObject(a)) return null
    let wf = null
    if (input === 'fastq') wf = fastqWorkflow(a)
    else if (input === 'vcf') wf = vcfWorkflow(a)
    else wf = longReadWorkflow(a, input)
    return wf ? complete(wf) : null
  }
  if (params.annotation_type === 'CARRIER' || analysisType === 'CARRIER') {
    const wf = carrierWorkflow(params, analysisName)
    return wf ? complete(wf) : null
  }
  if (analysisType === 'Multisample' && Array.isArray(params.samples)) return complete({ analysis: 'multisample' })
  // CNV cohorts name their kit by BedFile id (ref_bed_file_id), not by the name the
  // panel holds, and need a new name and members each time: not a setup to repeat.
  return null
}

/** The workflow of one /analysis/list row, or null. */
export const workflowOfRow = (row) =>
  row ? workflowFromParameters(row.analysis_parameters, row.analysis_type, row.analysis_name) : null

// ----------------------------------------------------------------------
// Signatures

const isExome = (wf) => wf.seq === 'wes' || wf.seq === 'ces'
const usesCohort = (method) => method === 'genesus-cnv' || method === 'exomedepth'

/**
 * A stable key of a workflow's batch-level choices: what has to match for two starts
 * to be the same setup. No sample fields, no batch, no display names. The default
 * tumour type is left out too: it describes the samples of a run (the panel does not
 * remember it either). Null for no workflow.
 */
export function signatureOf(wf) {
  if (!isObject(wf) || !wf.analysis) return null
  const parts = [wf.analysis]
  if (wf.analysis === 'carrier') parts.push(String(wf.carrierConfigId))
  else if (wf.analysis === 'germline' || wf.analysis === 'somatic') {
    parts.push(wf.input)
    if (wf.input === 'fastq') {
      if (wf.analysis === 'somatic') parts.push(wf.mode === 'tn' ? 'tn' : 'to')
      parts.push(wf.seq || '')
      if (isExome(wf)) parts.push(wf.kitId || '')
      // The cohort counts only for the methods that use one.
      if (wf.analysis === 'germline' && isExome(wf) && wf.cnv) {
        const cohort = usesCohort(wf.cnvMethod) ? String(wf.cnvCohortId ?? '') : ''
        parts.push(`cnv:${wf.cnvMethod}:${cohort}`)
      }
    } else if (wf.input === 'ont' || wf.input === 'pacbio') {
      parts.push(`cnv:${wf.lrCnv ? 1 : 0}`, `sv:${wf.lrSv ? 1 : 0}`, `meth:${wf.lrMeth ? 1 : 0}`)
    } else if (wf.input === 'vcf') parts.push(wf.reference || '')
    if (wf.analysis === 'germline') parts.push(wf.interpretation === 'ai' ? 'ai' : 'standard')
  }
  return JSON.stringify(parts)
}

/**
 * The key of a workflow without its per-sample switches (AI, CNV, long-read
 * options): the starts of one batch share it even when a sample was left out of AI
 * or CNV, or lacked methylation data.
 */
export const coreSignatureOf = (wf) =>
  isObject(wf)
    ? signatureOf({ ...wf, interpretation: 'standard', cnv: false, lrCnv: false, lrSv: false, lrMeth: false })
    : null
